/**
 * Counselor Authentication Guard
 * Protects counselor routes - only allows counselor role
 */

import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCounselorAuth } from '../../contexts/CounselorAuthContext';
import { AccessDenied } from './AccessDenied';
import { isCrmEnabled } from '../../utils/featureFlags';
import { CrmDisabled } from '../../pages/crm/CrmDisabled';

export const CounselorAuthGuard = ({ children }) => {
  const { user, loading, isAuthenticated } = useCounselorAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // If not authenticated, redirect to counselor login
  useEffect(() => {
    if (!isAuthenticated && !loading) {
      navigate('/crm/counselor/login', { state: { from: location.pathname } });
    }
  }, [isAuthenticated, loading, navigate, location.pathname]);

  // Check if CRM is enabled
  if (!isCrmEnabled()) {
    return <CrmDisabled />;
  }

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Verify user is counselor
  if (user && user.role !== 'counselor') {
    return (
      <AccessDenied
        requiredRole="counselor"
        userRole={user.role}
      />
    );
  }

  // User is authenticated and is counselor
  return <>{children}</>;
};
